import {
  AttendanceCustomTimeRangeDayEnum,
  GetCustomTimeRangesQuery,
} from 'generated/resources/graphql'
import { DeepPartial } from 'utility-types'

import { dateBackendDateTimeToUITimeRange } from 'modules/core/lib/dates'
import { minutesToText } from 'modules/core/lib/duration'

type AttendanceCustomTimeRange =
  GetCustomTimeRangesQuery['attendance']['customTimeRangesConnection']['nodes'][number]

class CustomTimeRangeUtils {
  static findMatchingRange<
    T extends Pick<
      AttendanceCustomTimeRange,
      'timeSettingsCustomTimeRangeCategoryId' | 'day' | 'startTime' | 'endTime' | 'poolType'
    >
  >(newCustomTimeRanges: T[], range?: T): T | undefined {
    return newCustomTimeRanges.find(
      (newCustomTimeRange) =>
        newCustomTimeRange.timeSettingsCustomTimeRangeCategoryId ===
          range?.timeSettingsCustomTimeRangeCategoryId &&
        newCustomTimeRange.day === range?.day &&
        newCustomTimeRange.startTime === range?.startTime &&
        newCustomTimeRange.endTime === range?.endTime &&
        newCustomTimeRange.poolType === range?.poolType
    )
  }

  static isRegular(customTimeRange: Pick<AttendanceCustomTimeRange, 'timeType'>) {
    return customTimeRange.timeType === 'regular'
  }

  static isFlexible(customTimeRange: Pick<AttendanceCustomTimeRange, 'timeType'>) {
    return customTimeRange.timeType === 'any_time'
  }

  static isOvertime(customTimeRange: Pick<AttendanceCustomTimeRange, 'timeType'>) {
    return customTimeRange.timeType === 'overtime'
  }

  static isCustom(customTimeRange: Pick<AttendanceCustomTimeRange, 'startTime' | 'endTime'>) {
    return !!customTimeRange.startTime && !!customTimeRange.endTime
  }

  static isBase(customTimeRange: Pick<AttendanceCustomTimeRange, 'startTime' | 'endTime'>) {
    return !this.isCustom(customTimeRange)
  }

  static hasTimeRanges<T extends DeepPartial<AttendanceCustomTimeRange>>(
    customTimeRanges: T[]
  ): boolean {
    return customTimeRanges.length > 0
  }

  static hasCustomTypeTimeRanges<T extends DeepPartial<AttendanceCustomTimeRange>>(
    customTimeRanges: T[]
  ): boolean {
    return customTimeRanges.some((range) => this.isCustom(range))
  }

  static hasBaseTypeTimeRanges<T extends DeepPartial<AttendanceCustomTimeRange>>(
    customTimeRanges: T[]
  ): boolean {
    return customTimeRanges.some((range) => this.isBase(range))
  }

  static baseTypeTimeRanges<T extends DeepPartial<AttendanceCustomTimeRange>>(
    customTimeRanges: T[]
  ) {
    return customTimeRanges.filter((range) => this.isBase(range))
  }

  static rawMinutes(customTimeRange: Pick<AttendanceCustomTimeRange, 'rawMinutesInCents'>) {
    return minutesToText(customTimeRange.rawMinutesInCents / 100)
  }

  static equivalentMinutes(
    customTimeRange: Pick<AttendanceCustomTimeRange, 'equivalentMinutesInCents'>
  ) {
    return minutesToText(customTimeRange.equivalentMinutesInCents / 100)
  }

  static customTypeTimeRanges<T extends DeepPartial<AttendanceCustomTimeRange>>(
    customTimeRanges: T[]
  ) {
    return customTimeRanges.filter((range) => this.isCustom(range))
  }

  static rangesInSpecificBaseDay<T extends DeepPartial<AttendanceCustomTimeRange>>(
    customTimeRanges: T[],
    day: AttendanceCustomTimeRangeDayEnum
  ) {
    return this.filterByDay(this.baseTypeTimeRanges(customTimeRanges), day)
  }

  static sortTimeRangesByStartTime<T extends Pick<AttendanceCustomTimeRange, 'startTime'>>(
    customTimeRanges: T[]
  ) {
    return customTimeRanges.sort((a, b) => a.startTime?.localeCompare(b?.startTime))
  }

  static getAllTimeRangesByType<T extends Pick<AttendanceCustomTimeRange, 'timeType'>>(
    customTimeRanges: T[]
  ) {
    const regularTimeRanges = customTimeRanges.filter((range) => this.isRegular(range))
    const flexibleTimeRanges = customTimeRanges.filter((range) => this.isFlexible(range))
    const overtimeTimeRanges = customTimeRanges.filter((range) => this.isOvertime(range))

    return {
      regular: regularTimeRanges,
      flexible: flexibleTimeRanges,
      overtime: overtimeTimeRanges,
    }
  }

  static rangesInSpecificCustomDay<T extends DeepPartial<AttendanceCustomTimeRange>>(
    customTimeRanges: T[],
    day: AttendanceCustomTimeRangeDayEnum
  ) {
    return this.sortTimeRangesByStartTime(
      this.filterByDay(this.customTypeTimeRanges(customTimeRanges), day)
    )
  }

  static hasRangesInSpecificDay<T extends DeepPartial<AttendanceCustomTimeRange>>(
    customTimeRanges: T[],
    day: AttendanceCustomTimeRangeDayEnum
  ): boolean {
    return (
      this.hasRangesInSpecificBaseDay(customTimeRanges, day) ||
      this.hasRangesInSpecificCustomDay(customTimeRanges, day)
    )
  }

  static filterByDay<T extends DeepPartial<AttendanceCustomTimeRange>>(
    customTimeRanges: T[],
    day: AttendanceCustomTimeRangeDayEnum
  ) {
    return customTimeRanges.filter((range) => range.day === day)
  }

  static hasRangesInSpecificBaseDay<T extends DeepPartial<AttendanceCustomTimeRange>>(
    customTimeRanges: T[],
    day: AttendanceCustomTimeRangeDayEnum
  ): boolean {
    return this.rangesInSpecificBaseDay(customTimeRanges, day).length > 0
  }

  static hasRangesInSpecificCustomDay<T extends DeepPartial<AttendanceCustomTimeRange>>(
    customTimeRanges: T[],
    day: AttendanceCustomTimeRangeDayEnum
  ): boolean {
    return this.rangesInSpecificCustomDay(customTimeRanges, day).length > 0
  }

  static rangeKey(customTimeRange: Pick<AttendanceCustomTimeRange, 'startTime' | 'endTime'>) {
    return this.isCustom(customTimeRange)
      ? dateBackendDateTimeToUITimeRange(customTimeRange.startTime, customTimeRange.endTime)
      : 'no_range'
  }

  static timeRangesByStartTime(customTimeRanges: AttendanceCustomTimeRange[]) {
    const initialValue: { [key: string]: AttendanceCustomTimeRange[] } = {}
    return this.customTypeTimeRanges(customTimeRanges).reduce(
      (acc: { [key: string]: AttendanceCustomTimeRange[] }, range: AttendanceCustomTimeRange) => {
        const startTime = range.startTime
        if (!acc[startTime]) {
          acc[startTime] = []
        }
        acc[startTime].push(range)
        return acc
      },
      initialValue
    )
  }

  static sortedStartTimes(timeRangesByStartTime: { [key: string]: AttendanceCustomTimeRange[] }) {
    return Object.keys(timeRangesByStartTime).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    )
  }
}

export default CustomTimeRangeUtils
